import { useEffect, useMemo, useRef } from "react";
import { ChevronLeft, Flag, RotateCcw } from "lucide-react";
import { useTurnRoom, type TurnRoom } from "../../lib/useTurnRoom";
import type { GameTableProps } from "../game-core";
import { othelloAdapter } from "./adapter";
import { CELLS, colOf, rowOf, type Disc, type OthelloMove, type OthelloState } from "./engine";

const COL_LABELS = "ABCDEFGH";
const coord = (cell: number) => `${COL_LABELS[colOf(cell)]}${rowOf(cell) + 1}`;

export function OthelloTable({ roomId, roomToken, myName, opponentName, opponentAvatarUrl, onExit, onProgress }: GameTableProps) {
  const room = useTurnRoom(othelloAdapter, "othello", roomId, roomToken);
  const { me, state, view, isMyTurn } = room;

  const myColor: Disc | null = me && view ? view.match.colors[me] ?? null : null;

  // Keep the shell's match list in sync with accepted (durable) state only. onProgress is read
  // through a ref so its per-render identity can't retrigger the effect (an infinite loop).
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  useEffect(() => {
    if (state && me) onProgressRef.current?.(othelloAdapter.summarize(state, me));
  }, [state, me]);

  // Cells the local player may legally place on this turn (empty when it's not their turn).
  const legalSet = useMemo(() => {
    if (!view || !me || !isMyTurn) return new Set<number>();
    const cells = othelloAdapter
      .legalMoves(view, me)
      .filter((m): m is { version: 1; type: "place"; cell: number } => m.type === "place")
      .map((m) => m.cell);
    return new Set(cells);
  }, [view, me, isMyTurn]);

  const mustPass = isMyTurn && view != null && !othelloAdapter.isTerminal(view) && legalSet.size === 0;
  const terminal = view != null && othelloAdapter.isTerminal(view);

  const myScore = view && myColor ? view.discCounts[myColor] : 0;
  const oppScore = view && myColor ? view.discCounts[myColor === "dark" ? "light" : "dark"] : 0;

  return (
    <article className="othello-table">
      <header className="table-bar">
        <button className="icon-button" onClick={onExit} aria-label="Back to games"><ChevronLeft /></button>
        <div className="table-title"><p className="eyebrow">Othello</p><h1>{opponentName ?? "Your game"}</h1></div>{opponentAvatarUrl && <img className="table-opponent-avatar" src={opponentAvatarUrl} alt={`${opponentName ?? "Opponent"}’s avatar`} />}
        <div className="disc-tally" aria-label={`You ${myScore}, opponent ${oppScore}`}>
          <span className="tally you"><i className="disc-dot you" />{myScore}</span>
          <span className="tally-sep">–</span>
          <span className="tally them">{oppScore}<i className="disc-dot them" /></span>
        </div>
      </header>

      <TurnStatus room={room} mustPass={mustPass} terminal={terminal} view={view} me={me} myName={myName} opponentName={opponentName} />

      <div className="othello-board-wrap">
        <div
          className="othello-board"
          role="grid"
          aria-label="Othello board, 8 by 8"
          aria-disabled={!isMyTurn || terminal}
        >
          {view
            ? Array.from({ length: CELLS }, (_, cell) => {
                const ch = view.board[cell];
                const disc: Disc | null = ch === "D" ? "dark" : ch === "L" ? "light" : null;
                const mine = disc != null && disc === myColor;
                const playable = legalSet.has(cell);
                const label = disc
                  ? `${coord(cell)}, ${mine ? "your disc" : "opponent disc"}`
                  : `${coord(cell)}, empty${playable ? ", playable" : ""}`;
                const flippedNow = view.lastMove?.type === "place" && view.lastMove.flipped.includes(cell);
                return (
                  <button
                    key={cell}
                    role="gridcell"
                    className={`othello-cell${playable ? " playable" : ""}`}
                    aria-label={label}
                    disabled={!playable}
                    onClick={() => playable && void room.submit({ version: 1, type: "place", cell })}
                  >
                    {disc && <i className={`disc ${mine ? "you" : "them"}${flippedNow ? " flipped" : ""}`} />}
                    {playable && !disc && <span className="hint-dot" aria-hidden="true" />}
                  </button>
                );
              })
            : <div className="board-skeleton" aria-hidden="true" />}
        </div>
      </div>

      <ActivityStrip view={view} me={me} myName={myName} opponentName={opponentName} />

      <footer className="table-actions">
        {mustPass && (
          <button className="primary-button" onClick={() => void room.submit({ version: 1, type: "pass" })}>
            No legal move — pass
          </button>
        )}
        {!terminal && view && (
          <button
            className="ghost-button"
            onClick={() => { if (confirm("Resign this game? Your opponent wins immediately.")) void room.submit({ version: 1, type: "resign" }); }}
          >
            <Flag size={16} /> Resign
          </button>
        )}
        {terminal && (
          <button className="primary-button" onClick={onExit}><RotateCcw size={16} /> Back to games</button>
        )}
      </footer>

      {room.error && <p className="form-error" role="alert">{room.error}</p>}
    </article>
  );
}

function TurnStatus({
  room, mustPass, terminal, view, me, myName, opponentName,
}: {
  room: TurnRoom<OthelloState, OthelloMove>;
  mustPass: boolean;
  terminal: boolean;
  view: OthelloState | null;
  me: string | null;
  myName: string;
  opponentName?: string;
}) {
  let message: string;
  let tone = "waiting";
  if (room.waiting === "connecting") message = "Connecting to the table…";
  else if (room.waiting === "opponent" && room.isMyTurn) {
    message = "Take the opening move, then invite your opponent.";
    tone = "your-turn";
  }
  else if (room.waiting === "opponent") message = "Waiting for your opponent to join.";
  else if (terminal && view && me) {
    const result = othelloAdapter.summarize(view, me).result;
    message = result === "won" ? "You won!" : result === "lost" ? `${opponentName ?? "Your opponent"} won.` : "It’s a draw.";
    tone = result === "won" ? "your-turn" : "waiting";
  } else if (mustPass) { message = "You have no legal move — you must pass."; tone = "your-turn"; }
  else if (room.isMyTurn) { message = "Your turn — place a disc."; tone = "your-turn"; }
  else message = `Waiting for ${opponentName ?? "your opponent"}.`;

  return (
    <div className={`turn-status ${tone}`} role="status" aria-live="polite">
      <span className="turn-dot" aria-hidden="true" />
      <span>{message}</span>
      <span className="turn-you">{myName}</span>
    </div>
  );
}

function ActivityStrip({
  view, me, myName, opponentName,
}: {
  view: OthelloState | null;
  me: string | null;
  myName: string;
  opponentName?: string;
}) {
  const last = view?.lastMove;
  if (!last || !me) return <div className="activity-strip" aria-hidden="true" />;
  const who = last.playerId === me ? myName : opponentName ?? "Opponent";
  let text: string;
  if (last.type === "place") text = `${who} played ${coord(last.cell)}, flipping ${last.flipped.length}.`;
  else if (last.type === "pass") text = `${who} passed.`;
  else text = `${who} resigned.`;
  return <div className="activity-strip" role="status" aria-live="polite">{text}</div>;
}
